import { PageHeader } from "@shared/components/PageHeader";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { format } from "date-fns";
import { ArrowLeft, HelpCircle, CheckSquare } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ConsumerLookup } from "./ConsumerLookup";

import { toast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import {
  useCreatePayment,
  useInstallments,
  usePaymentChannelChoices,
  usePaymentPayTypeChoices,
  usePaymentServices,
  usePendingBills,
  useServiceOrder,
} from "../../hooks";
import { PaymentReceipt } from "../Detail/PaymentReceipt";
import { ApplyCreditBalance } from "./ApplyCredit";
import { PaymentDetailsForm } from "./PaymentDetailsForm";
import { PaymentModeForm } from "./PaymentModeForm";
import { PaymentSummary } from "./PaymentSummary";
import { SelectedConsumerCard } from "./SelectedConsumerCard";
import {
  useConsumerDetails,
  useConsumerDetailsforPayment,
} from "@features/cx/consumer-onboarding/hooks";

export const RecordNewPayment: React.FC = () => {
  const navigate = useNavigate();
  const currency = useCurrency();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const searchConsumerNo = searchParams.get("search");
  // Query params set by the Pay Now button in service details
  const paymentTypeParam = searchParams.get("paymentType") ?? "";
  const serviceIdParam = searchParams.get("serviceId") ?? "";

  const {
    consumerNumber: oldConsumerNumber,
    paymentType: paymentTypeValue,
    installmentId,
    prefilledConsumer,
    source: stateSource,
  } = location.state || {};

  const source = stateSource || searchParams.get("source");

  // Resolve consumer number from query params, standardized state, or legacy state
  const consumerNumber =
    searchConsumerNo || prefilledConsumer?.consumerNo || oldConsumerNumber;

  // Effective payment type: prefer location.state, then query param
  const effectivePaymentType = paymentTypeValue || paymentTypeParam || "";

  const [selectedConsumer, setSelectedConsumer] = useState<any | null>(null);
  const [wasManuallyRemoved, setWasManuallyRemoved] = useState(false);
  console.log("selected consumer", selectedConsumer);

  const { data: consumerDetailsData } = useConsumerDetailsforPayment({
    remote_utility_id: String(getRemoteUtilityId()),
    consumer_id: String(selectedConsumer?.id),
    is_detail_info: true,
  });
  const { data: installments } = useInstallments(
    Number(selectedConsumer?.id),
    selectedConsumer?.consumerNo,
  );
  const { data: serviceOrderPayments } = useServiceOrder(selectedConsumer?.id);
  const { data: servicePayments } = usePaymentServices({
    remoteUtilityId: getRemoteUtilityId(),
    consumerId: Number(selectedConsumer?.id),
  });

  const { data: paymentChannel } =
    usePaymentChannelChoices(getRemoteUtilityId());

  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [createdPaymentId, setCreatedPaymentId] = useState<number | null>(null);

  const cachedUserProfile = localStorage.getItem("cached_user_profile");
  const userProfile = cachedUserProfile ? JSON.parse(cachedUserProfile) : null;

  const createPayment = useCreatePayment(
    getRemoteUtilityId(),
    selectedConsumer?.consumerId,
    selectedConsumer?.consumerNo,
  );
  const { data: paymentType } = usePaymentPayTypeChoices(getRemoteUtilityId());
  // Simple Record for all form data
  const [formData, setFormData] = useState<Record<string, any>>({
    paymentChannel: "",
    paymentType: effectivePaymentType,
    selectedBills: [],
    selectedInstallments: installmentId ? [installmentId] : [],
    selectedServiceOrders: [],
    selectedServicePayments: [],
    amount: 0,
    creditApplied: 0,
    applyCreditBalance: false,
    paymentMode: "",
    totalPayableAmount: 0,
    cashReceived: 0,
    amountReceived: 0,
    paymentDate: format(new Date(), "yyyy-MM-dd"),
    recordedBy: userProfile?.user?.name,
    lateFeesWaived: {},
    createCreditNote: false,
    lateFee: 0,
    isLateFeeWaived: false,
    linkedBillId: "",
    remoteBillId: 0,
  });

  console.log("form data", formData);

  // Define Payment Details Form Fields
  const paymentDetailsFields: FormField[] = useMemo(
    () => [
      {
        name: "paymentChannel",
        label: "Payment Channel",
        type: "select",
        required: true,
        placeholder: "Select channel",
        options:
          paymentChannel?.result?.map((channel) => ({
            value: channel.code,
            label: channel.name,
          })) || [],
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText: "Select how the payment was received",
      },
      {
        name: "paymentType",
        label: "Payment Type",
        type: "select",
        required: true,
        defaultValue: effectivePaymentType,
        placeholder: "Select type",
        options:
          paymentType?.result?.map((type) => ({
            value: type.key,
            label: type.value,
          })) || [],
        classes: FIELD_CLASSES,
        showHelperTooltip: true,
        tooltipText: "Select the type of payment",
      },
    ],
    [paymentChannel, paymentType],
  );

  // Create Payment Details Form Instance
  const paymentDetailsForm = FormService.createForm({
    fields: paymentDetailsFields,
    defaultValues: {
      paymentChannel: formData.paymentChannel,
      paymentType: formData.paymentType,
      amount: formData.amount,
    },
  });

  // Create Payment Mode Form Instance
  const paymentModeForm = FormService.createForm({
    fields: [],
    defaultValues: {
      paymentMode: formData.paymentMode,
      paymentDate: formData.paymentDate,
    },
  });

  const { data: pendingBills } = usePendingBills({
    remoteUtilityId: getRemoteUtilityId(),
    consumerId: Number(selectedConsumer?.id),
  });

  // Auto-populate payment type dropdown on the form after consumer is selected
  useEffect(() => {
    if (selectedConsumer && effectivePaymentType) {
      paymentDetailsForm.setValue("paymentType", effectivePaymentType);
    }
  }, [selectedConsumer, effectivePaymentType]);

  // Auto-select the service order matching serviceIdParam once data is loaded
  useEffect(() => {
    if (
      paymentTypeParam === "5" &&
      serviceIdParam &&
      selectedConsumer &&
      serviceOrderPayments &&
      serviceOrderPayments.length > 0 &&
      formData.selectedServiceOrders.length === 0
    ) {
      const match = serviceOrderPayments.find(
        (so: any) => String(so.id) === String(serviceIdParam),
      );
      if (match) {
        handleServiceOrderToggle(match.id);
      }
    }
  }, [serviceOrderPayments, selectedConsumer]);

  // Calculate amounts based on selections
  useEffect(() => {
    let calculatedAmount = 0;
    let lateFee = 0;
    let linkedId = "";
    let remoteBillId = 0;

    // Calculate based on payment type

    if (formData.paymentType === "2" && formData.selectedBills.length > 0) {
      const selectedBillsList =
        pendingBills?.pendingBills?.filter((b: any) =>
          formData.selectedBills.includes(b.invoiceNo),
        ) || [];

      if (selectedBillsList.length > 0) {
        calculatedAmount = selectedBillsList.reduce(
          (sum: number, b: any) => sum + Number(b.outstandingAmount || 0),
          0,
        );
        linkedId = selectedBillsList.map((b: any) => b.invoiceNo).join(", ");
        remoteBillId = selectedBillsList[0].remoteBillId;

        // Add late fee if bill is overdue and not waived
        if (!formData.isLateFeeWaived) {
          lateFee = selectedBillsList
            .filter(
              (b: any) =>
                String(b.billStatus || "").toUpperCase() === "OVERDUE",
            )
            .reduce((sum: number, b: any) => {
              const fee =
                b.lateFee != null && b.lateFee > 0
                  ? Number(b.lateFee)
                  : Number(b.outstandingAmount || 0) * 0.05;
              return sum + fee;
            }, 0);
          calculatedAmount += lateFee;
        }
      }
    } else if (
      formData.paymentType === "6" &&
      formData.selectedInstallments.length > 0
    ) {
      const selectedInstallment = installments?.find(
        (i: any) => String(i.id) === String(formData.selectedInstallments[0]),
      );
      if (selectedInstallment) {
        calculatedAmount =
          selectedInstallment.installment_amount ??
          selectedInstallment.installmentAmount ??
          selectedInstallment.amount ??
          0;
        linkedId =
          selectedInstallment.agreement_no ??
          selectedInstallment.agreementNo ??
          selectedInstallment.agreement_id ??
          selectedInstallment.agreementId ??
          selectedInstallment.id;
      }
    } else if (
      formData.paymentType === "7" &&
      formData.selectedServiceOrders.length > 0
    ) {
      const selectedServiceOrder = serviceOrderPayments?.find(
        (s: any) => String(s.id) === String(formData.selectedServiceOrders[0]),
      );
      if (selectedServiceOrder) {
        calculatedAmount = Number(
          selectedServiceOrder.totalActualCost ??
            selectedServiceOrder.totalAmount ??
            0,
        );
        linkedId = selectedServiceOrder.serviceOrderId;
      }
    } else if (
      formData.paymentType === "5" &&
      formData.selectedServicePayments.length > 0
    ) {
      const selectedServicePayment = servicePayments?.result?.find(
        (s: any) =>
          String(s.id) === String(formData.selectedServicePayments[0]),
      );
      if (selectedServicePayment) {
        calculatedAmount = Number(
          selectedServicePayment.serviceCharge ??
            selectedServicePayment.amount ??
            selectedServicePayment.totalAmount ??
            0,
        );
        linkedId = selectedServicePayment.requestNo;
      }
    } else if (String(formData.paymentType) === "10") {
      calculatedAmount =
        consumerDetailsData?.result?.additionalData?.planExtraData?.totalDue ||
        0;
    }

    setFormData((prev) => ({
      ...prev,
      amount: calculatedAmount,
      lateFee,
      linkedBillId: linkedId,
      remoteBillId: remoteBillId,
    }));

    paymentDetailsForm.setValue("amount", calculatedAmount);
  }, [
    formData.paymentType,
    formData.selectedBills,
    formData.selectedInstallments,
    formData.selectedServiceOrders,
    formData.selectedServicePayments,
    formData.isLateFeeWaived,
    pendingBills?.pendingBills,
    paymentDetailsForm,
    pendingBills,
    installments,
    serviceOrderPayments,
    consumerDetailsData,
  ]);

  // Calculate credit applied and total payable
  useEffect(() => {
    if (!selectedConsumer || !formData.applyCreditBalance) {
      setFormData((prev) => ({
        ...prev,
        creditApplied: 0,
        totalPayableAmount: prev.amount,
      }));
      return;
    }

    const currentAmount = Number(formData.amount) || 0;
    const availableCredit = selectedConsumer.creditBalance ?? 0;
    const creditToApply = Math.min(availableCredit, currentAmount);
    const totalPayable = Math.max(0, currentAmount - creditToApply);

    setFormData((prev) => ({
      ...prev,
      creditApplied: creditToApply,
      totalPayableAmount: totalPayable,
    }));
  }, [formData.amount, formData.applyCreditBalance, selectedConsumer]);

  // Handle field change from child components
  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    // Reset selections when payment type changes
    if (fieldName === "paymentType") {
      setFormData((prev) => ({
        ...prev,
        selectedBills: [],
        selectedInstallments: [],
        selectedServiceOrders: [],
        selectedServicePayments: [],
        amount: 0,
        lateFeesWaived: {},
      }));
    }
  };

  // Handle bill toggle (single or multi selection)
  const handleBillToggle = (billId: string) => {
    setFormData((prev) => {
      const isMulti = String(prev.paymentType) === "2";
      if (isMulti) {
        const isSelected = prev.selectedBills.includes(billId);
        return {
          ...prev,
          selectedBills: isSelected
            ? prev.selectedBills.filter((id: string) => id !== billId)
            : [...prev.selectedBills, billId],
        };
      } else {
        return {
          ...prev,
          selectedBills: prev.selectedBills.includes(billId) ? [] : [billId],
        };
      }
    });
  };

  const handleSelectAllBills = (billIds: string[]) => {
    setFormData((prev) => ({
      ...prev,
      selectedBills: billIds,
    }));
  };

  // Handle installment toggle (single selection)
  const handleInstallmentToggle = (installmentId: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedInstallments: prev.selectedInstallments.includes(installmentId)
        ? []
        : [installmentId],
    }));
  };

  // Handle service order toggle (single selection)
  const handleServiceOrderToggle = (serviceOrderId: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedServiceOrders: prev.selectedServiceOrders.includes(serviceOrderId)
        ? []
        : [serviceOrderId],
    }));
  };

  const handleServicePaymentToggle = (servicePaymentId: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedServicePayments: prev.selectedServicePayments.includes(
        servicePaymentId,
      )
        ? []
        : [servicePaymentId],
    }));
  };

  // Handle late fee waiver
  const handleWaiveLateFee = (waived: boolean) => {
    setFormData((prev) => ({
      ...prev,
      isLateFeeWaived: waived,
    }));
  };

  // Handle apply credit balance toggle
  const handleApplyCreditToggle = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      applyCreditBalance: checked,
    }));
  };

  // Handle create credit note toggle
  const handleCreateCreditNoteToggle = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      createCreditNote: checked,
    }));
  };

  const handleConsumerSelect = (consumer: any) => {
    setSelectedConsumer(consumer);
  };

  const handleConsumerRemove = () => {
    setWasManuallyRemoved(true);
    setSelectedConsumer(null);
    setFormData({
      paymentChannel: "",
      paymentType: "",
      selectedBills: [],
      selectedInstallments: [],
      selectedServiceOrders: [],
      selectedServicePayments: [],
      amount: 0,
      creditApplied: 0,
      applyCreditBalance: false,
      paymentMode: "",
      totalPayableAmount: 0,
      cashReceived: 0,
      amountReceived: 0,
      paymentDate: format(new Date(), "yyyy-MM-dd"),
      recordedBy: "User",
      lateFeesWaived: {},
      createCreditNote: false,
      lateFee: 0,
      isLateFeeWaived: false,
      linkedBillId: "",
      remoteBillId: 0,
    });
    paymentDetailsForm.reset();
    paymentModeForm.reset();
  };

  const handleCancel = () => {
    navigate(`/cx/payments`);
  };

  const handleConfirm = async () => {
    // Validate payment details form
    const isPaymentDetailsValid =
      await FormService.validateForm(paymentDetailsForm);

    if (!isPaymentDetailsValid) {
      return;
    }

    // Check if entity is selected (payment type 10 doesn't require an entity selection)
    const isEntitySelected =
      String(formData.paymentType) === "10" ||
      formData.selectedBills.length > 0 ||
      formData.selectedInstallments.length > 0 ||
      formData.selectedServiceOrders.length > 0 ||
      formData.selectedServicePayments.length > 0;

    if (!isEntitySelected) {
      return;
    }

    // Validate payment mode if applicable
    if (formData.totalPayableAmount > 0) {
      const isPaymentModeValid =
        await FormService.validateForm(paymentModeForm);
      if (!isPaymentModeValid) {
        return;
      }
    }

    // Get selected entity details
    let selectedEntity = null;
    let entityType = "";

    const paymentModeData = paymentModeForm.getValues();

    // Calculate refund/outstanding
    const rawCashReceived =
      paymentModeData?.cashReceived ??
      paymentModeData?.cash_received ??
      formData.cashReceived;

    const amountReceived =
      rawCashReceived != null && Number(rawCashReceived) > 0
        ? Number(rawCashReceived)
        : Number(
            formData.amountReceived ||
              formData.cashReceived ||
              formData.totalPayment ||
              formData.amount ||
              0,
          );

    const difference = amountReceived - formData.totalPayableAmount;
    const isRefund = difference > 0;
    const isOutstanding = difference < 0;

    const findValue = (keywords: string[]) => {
      for (const [key, value] of Object.entries(paymentModeData)) {
        const lowerKey = key.toLowerCase();
        if (keywords.every((kw) => lowerKey.includes(kw))) {
          return value;
        }
      }
      return undefined;
    };

    const chequeNumber =
      formData.chequeNumber ||
      paymentModeData.cheque_number ||
      paymentModeData.chequeNumber ||
      paymentModeData["cheque number"] ||
      findValue(["cheque", "number"]) ||
      findValue(["cheque", "no"]);

    const chequeDate =
      formData.chequeDate ||
      paymentModeData.cheque_date ||
      paymentModeData.chequeDate ||
      paymentModeData["cheque date"] ||
      findValue(["cheque", "date"]);

    const drawerBank =
      formData.drawerBank ||
      formData.bankName ||
      paymentModeData.drawer_bank ||
      paymentModeData.bankName ||
      paymentModeData["bank name"] ||
      findValue(["drawer", "bank"]) ||
      findValue(["bank", "name"]) ||
      findValue(["bank"]);

    let paymentPayload: any = {};

    if (String(formData.paymentType) === "2") {
      const selectedBillsList =
        pendingBills?.pendingBills?.filter((b: any) =>
          formData.selectedBills.includes(b.invoiceNo),
        ) || [];

      const rootLateFeeAmount = selectedBillsList
        .filter(
          (b: any) => String(b.billStatus || "").toUpperCase() === "OVERDUE",
        )
        .reduce((sum: number, b: any) => {
          const fee =
            b.lateFee != null && b.lateFee > 0
              ? Number(b.lateFee)
              : Number(b.outstandingAmount || 0) * 0.05;
          return sum + fee;
        }, 0);

      if (selectedBillsList.length === 1) {
        const b = selectedBillsList[0];
        const isOverdue =
          String(b.billStatus || "").toUpperCase() === "OVERDUE";
        const outstanding = Number(b.outstandingAmount || 0);
        const actualLateFee = isOverdue
          ? b.lateFee != null && b.lateFee > 0
            ? Number(b.lateFee)
            : outstanding * 0.05
          : 0;

        let billAmountPaid = outstanding;
        if (isOverdue && !formData.isLateFeeWaived) {
          billAmountPaid += actualLateFee;
        }

        paymentPayload = {
          consumer: selectedConsumer.id,
          remote_utility_id: getRemoteUtilityId(),
          payment_channel: formData.paymentChannel,
          payment_mode: formData.paymentMode,
          payment_date: formData.paymentDate,
          cheque_date: formData.paymentDate,
          payment_pay_type: formData.paymentType,
          source: 1,
          payment_received_status: 1,
          status: "CREDIT",
          remote_bill_id: b.remoteBillId,
          amount: Number(formData.totalPayment || billAmountPaid),
          create_credit_note: Boolean(formData.createCreditNote),
          extra_data: {
            bill_amount: Number(
              formData.totalPayableAmount || billAmountPaid,
            ).toFixed(2),
            payment_amount: amountReceived,
            excess_refund: isRefund ? Number(difference.toFixed(2)) : 0,
            billing_period: b.billPeriod || "",
          },
          late_fee_amount: rootLateFeeAmount.toFixed(2),
          late_fee_waived: formData.isLateFeeWaived || false,
          apply_credit: formData.applyCreditBalance || false,
          credit_amount: Number(formData.creditApplied || 0).toFixed(2),
          total_amount: Number(formData.totalPayableAmount || 0).toFixed(2),
          cash_received: Number(amountReceived || 0).toFixed(2),
        };
      } else {
        paymentPayload = {
          consumer: selectedConsumer.id,
          remote_utility_id: getRemoteUtilityId(),
          payment_channel: formData.paymentChannel,
          payment_mode: formData.paymentMode,
          payment_date: formData.paymentDate,
          payment_pay_type: formData.paymentType,
          source: 1,
          payment_received_status: 1,
          status: "CREDIT",
          bills: selectedBillsList.map((b: any) => {
            const isOverdue =
              String(b.billStatus || "").toUpperCase() === "OVERDUE";
            const outstanding = Number(b.outstandingAmount || 0);
            const actualLateFee = isOverdue
              ? b.lateFee != null && b.lateFee > 0
                ? Number(b.lateFee)
                : outstanding * 0.05
              : 0;

            let billAmountPaid = outstanding;
            if (isOverdue && !formData.isLateFeeWaived) {
              billAmountPaid += actualLateFee;
            }

            return {
              remote_bill_id: b.remoteBillId || 0,
              bill_number: b.invoiceNo || b.billNo || "",
              billing_period: b.billPeriod || "",
              bill_status: (b.billStatus || "").toUpperCase(),
              outstanding_amount: outstanding.toFixed(2),
              amount_paid: billAmountPaid.toFixed(2),
              late_fee_amount: actualLateFee.toFixed(2),
              late_fee_waived: formData.isLateFeeWaived || false,
            };
          }),
          late_fee_amount: rootLateFeeAmount.toFixed(2),
          late_fee_waived: formData.isLateFeeWaived || false,
          apply_credit: formData.applyCreditBalance || false,
          credit_amount: Number(formData.creditApplied || 0).toFixed(2),
          total_amount: Number(formData.totalPayableAmount || 0).toFixed(2),
          cash_received: Number(amountReceived || 0).toFixed(2),
          extra_data: {
            bill_amount: Number(formData.totalPayableAmount || 0).toFixed(2),
            payment_amount: amountReceived,
            excess_refund: isRefund ? Number(difference.toFixed(2)) : 0,
            selected_bills: formData.selectedBills,
          },
        };
      }

      if (userProfile?.user?.id)
        paymentPayload.created_user_remote_id = String(userProfile.user.id);
      if (userProfile?.user?.name)
        paymentPayload.created_user_remote_name = userProfile.user.name;
      if (chequeNumber) paymentPayload.cheque_number = chequeNumber;
      if (chequeDate) paymentPayload.cheque_date = chequeDate;
      if (drawerBank) paymentPayload.drawer_bank = drawerBank;
    } else {
      paymentPayload = {
        remote_utility_id: getRemoteUtilityId(),
        consumer: selectedConsumer.id,
        payment_mode: formData.paymentMode,
        amount: amountReceived,
        cheque_number: chequeNumber,
        cheque_date: chequeDate,
        drawer_bank: drawerBank,
        payment_date: formData.paymentDate,
        payment_pay_type: formData.paymentType,
        payment_channel: formData.paymentChannel,
        source: 1,
        payment_received_status: 1,
        status: "CREDIT",
        create_credit_note: formData.createCreditNote,
        remote_bill_id: formData.remoteBillId || 0,
        extra_data: {
          bill_amount: formData.totalPayableAmount,
          payment_amount: amountReceived,
          excess_refund: isRefund ? Number(difference.toFixed(2)) : 0,
          selected_bills: formData.selectedBills,
        },
      };

      if (
        String(formData.paymentType) === "6" &&
        formData.selectedInstallments?.length > 0
      ) {
        paymentPayload.payment_installment = Number(
          formData.selectedInstallments[0],
        );
      }

      if (
        String(formData.paymentType) === "5" &&
        formData.selectedServicePayments?.length > 0
      ) {
        paymentPayload.consumer_support_request = Number(
          formData.selectedServicePayments[0],
        );
      }
    }

    createPayment.mutate(paymentPayload, {
      onSuccess: (response) => {
        toast({
          title: "Payment Successful",
          description: "Payment has been created successfully",
        });

        const paymentId = response?.result?.paymentId || response?.result?.id;

        if (paymentId) {
          setCreatedPaymentId(paymentId);
          setIsReceiptModalOpen(true);
        } else {
          // Fallback just in case ID is completely missing but we want to show success
          console.warn(
            "Payment created but ID not found in response",
            response,
          );
          setIsReceiptModalOpen(true);
        }
      },
    });

    // Log payment mode specific fields

    const completePayload = {
      consumer: selectedConsumer,
      paymentDetails: {
        channel: formData.paymentChannel,
        type: entityType,
        selectedEntity: selectedEntity,
        linkedTo: formData.linkedBillId,
      },
      amounts: {
        baseAmount: formData.amount,
        lateFee: formData.lateFee,
        lateFeeWaived: formData.isLateFeeWaived,
        creditApplied: formData.creditApplied,
        totalPayableAmount: formData.totalPayableAmount,
        amountReceived: amountReceived,
        refundOrOutstanding: {
          type: isRefund ? "Refund" : isOutstanding ? "Outstanding" : "Exact",
          amount: Math.abs(difference),
        },
      },
      paymentMode: {
        mode: formData.paymentMode,
        fields: paymentModeData,
      },
      creditNote: {
        create: formData.createCreditNote,
        amount: isRefund ? Math.abs(difference) : 0,
      },
      metadata: {
        currency,
        paymentDate: formData.paymentDate,
        recordedBy: formData.recordedBy,
        submittedAt: new Date().toISOString(),
      },
    };

    // Simulate API call
  };

  return (
    <div>
      <div>
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <Button onClick={() => navigate(-1)} variant="ghost">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <PageHeader
              title="Record New Payment"
              subtitle="Enter payment details for a consumer"
            />
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-medium">
              1
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              Consumer Lookup
            </h2>
            <HelpCircle className="w-4 h-4 text-gray-400" />
          </div>

          {selectedConsumer ? (
            <SelectedConsumerCard
              consumer={selectedConsumer}
              onRemove={handleConsumerRemove}
              currency={currency}
            />
          ) : (
            <ConsumerLookup
              key={selectedConsumer ? "has-consumer" : "no-consumer"}
              onConsumerSelect={handleConsumerSelect}
              consumerNumber={wasManuallyRemoved ? undefined : consumerNumber}
              source={source}
            />
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Forms */}
          <div className="lg:col-span-2 space-y-6 max-h-[800px] overflow-y-auto">
            {/* Step 1: Consumer Lookup */}

            {/* Show forms only when consumer is selected */}
            {selectedConsumer && (
              <>
                {/* Step 2: Payment Details */}
                <PaymentDetailsForm
                  form={paymentDetailsForm}
                  fields={paymentDetailsFields}
                  onFieldChange={handleFieldChange}
                  paymentType={formData.paymentType}
                  selectedBills={formData.selectedBills}
                  selectedInstallments={formData.selectedInstallments}
                  selectedServiceOrders={formData.selectedServiceOrders}
                  selectedServicePayments={formData.selectedServicePayments}
                  onBillToggle={handleBillToggle}
                  onInstallmentToggle={handleInstallmentToggle}
                  onServiceOrderToggle={handleServiceOrderToggle}
                  onServicePaymentToggle={handleServicePaymentToggle}
                  lateFeesWaived={formData.isLateFeeWaived}
                  onWaiveLateFee={handleWaiveLateFee}
                  onSelectAllBills={handleSelectAllBills}
                  currency={currency}
                  consumer={{
                    consumerId: selectedConsumer.id,
                    consumerNo: selectedConsumer.consumerNo,
                  }}
                  installments={installments ?? []}
                  serviceOrderPayments={serviceOrderPayments ?? []}
                  servicePayments={servicePayments?.result ?? []}
                />

                {/* NSC Charges Summary */}
                {String(formData.paymentType) === "10" &&
                  consumerDetailsData?.result?.additionalData
                    ?.planExtraData && (
                    <div className="bg-[#f0fdf4] rounded-lg border border-green-200 p-6 mt-6">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckSquare className="w-5 h-5 text-green-700" />
                        <h3 className="text-lg font-bold text-green-800">
                          New Service Connection (NSC) Charges
                        </h3>
                      </div>
                      <p className="text-sm text-green-700 mb-4">
                        All applicable charges for this new service connection
                        are listed below. These amounts are standardised and
                        cannot be edited.
                      </p>
                      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        {consumerDetailsData?.result?.additionalData?.planExtraData?.charges?.map(
                          (charge: any, index: number) => (
                            <div
                              key={index}
                              className="p-4 border-b border-gray-200 flex justify-between items-center"
                            >
                              <div>
                                <div className="font-bold text-gray-900 text-sm">
                                  {charge.chargeName}
                                </div>
                              </div>
                              <div className="font-bold text-blue-900">
                                {currency}
                                {charge.amount || "0.00"}
                              </div>
                            </div>
                          ),
                        )}
                        {(!consumerDetailsData?.result?.additionalData
                          ?.planExtraData?.charges ||
                          consumerDetailsData.result.additionalData
                            .planExtraData.charges.length === 0) && (
                          <div className="p-4 border-b border-gray-200 text-sm text-gray-500 italic">
                            No registration charges found.
                          </div>
                        )}
                        <div className="p-4 bg-[#f0fdf4] flex justify-between items-center">
                          <div className="font-bold text-green-800">
                            Total NSC Charges
                          </div>
                          <div className="font-bold text-green-800">
                            {currency}
                            {consumerDetailsData?.result?.additionalData
                              ?.planExtraData?.totalDue || "0.00"}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Apply Credit Balance - Show only if amount > 0 */}
                {formData.amount > 0 && (
                  <ApplyCreditBalance
                    availableCredit={selectedConsumer.creditBalance}
                    isApplied={formData.applyCreditBalance}
                    onToggle={handleApplyCreditToggle}
                    currency={currency}
                    billAmount={formData.amount}
                    creditApplied={formData.creditApplied}
                    remainingCredit={
                      selectedConsumer.creditBalance - formData.creditApplied
                    }
                    totalPayable={formData.totalPayableAmount}
                  />
                )}

                {/* Step 3: Payment Mode - Only show if totalPayable > 0 */}
                {(!formData.applyCreditBalance ||
                  formData.amount - formData.creditApplied > 0) && (
                  <PaymentModeForm
                    form={paymentModeForm}
                    onFieldChange={handleFieldChange}
                    currency={currency}
                    totalPayableAmount={formData.totalPayableAmount}
                    amountReceived={Number(
                      formData.totalPayment ||
                        formData.amountReceived ||
                        formData.cashReceived ||
                        0,
                    )}
                    paymentMode={formData.paymentMode}
                    paymentType={formData.paymentType}
                    createCreditNote={formData.createCreditNote}
                    onCreateCreditNoteToggle={handleCreateCreditNoteToggle}
                  />
                )}
              </>
            )}
          </div>

          {selectedConsumer && (
            <div className="lg:col-span-1 sticky top-6">
              <PaymentSummary
                consumer={selectedConsumer}
                formData={formData}
                pendingBills={pendingBills?.pendingBills || []}
                onCancel={handleCancel}
                onConfirm={handleConfirm}
                isSubmitting={createPayment.isPending}
                currency={currency}
              />
            </div>
          )}
        </div>
      </div>
      {createdPaymentId && (
        <PaymentReceipt
          formData={formData}
          isOpen={isReceiptModalOpen}
          onClose={() => {
            setIsReceiptModalOpen(false);
            navigate("/cx/payments");
          }}
          paymentId={createdPaymentId}
        />
      )}
    </div>
  );
};
